import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useI18n } from "@/lib/i18n";
import { useAuth, homeFor } from "@/lib/auth";
import { api, errMsg } from "@/lib/api";

function Shell({ title, children }) {
  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4 py-16">
      <div className="w-full max-w-md rounded-3xl bg-white border border-slate-200 p-8 fade-up">
        <img src="/img/logo.png" alt="NicaFix logo" className="h-20 w-auto" data-testid="auth-logo" />
        <h1 className="mt-5 text-3xl font-extrabold tracking-tight text-slate-900">{title}</h1>
        {children}
      </div>
    </div>
  );
}

export function Login() {
  const { t } = useI18n();
  const { signIn } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [f, setF] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/auth/login", f);
      signIn(r.data);
      nav(sp.get("next") || homeFor(r.data.user.role));
    } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };
  return (
    <Shell title={t("login.title")}>
      <form onSubmit={submit} className="mt-6 space-y-4" data-testid="login-form">
        <div className="space-y-1.5"><Label>{t("f.email")}</Label><Input type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="login-email-input" /></div>
        <div className="space-y-1.5"><Label>{t("f.password_login")}</Label><Input type="password" required value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="login-password-input" /></div>
        <Button type="submit" disabled={busy} className="w-full h-11 rounded-full bg-teal-600 hover:bg-teal-700" data-testid="login-submit-btn">{busy && <Loader2 className="h-4 w-4 me-2 animate-spin" />}{t("login.submit")}</Button>
      </form>
      <Link to="/register" className="block mt-5 text-sm text-teal-700 hover:underline" data-testid="goto-register-link">{t("login.noacct")}</Link>
    </Shell>
  );
}

export function Register() {
  const { t } = useI18n();
  const { signIn } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ name: "", email: "", phone: "", password: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { const r = await api.post("/auth/register", f); signIn(r.data); nav("/dashboard"); }
    catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };
  return (
    <Shell title={t("register.title")}>
      <form onSubmit={submit} className="mt-6 space-y-4" data-testid="register-form">
        <div className="space-y-1.5"><Label>{t("f.name")}</Label><Input required value={f.name} onChange={set("name")} data-testid="register-name-input" /></div>
        <div className="space-y-1.5"><Label>{t("f.email")}</Label><Input type="email" required value={f.email} onChange={set("email")} data-testid="register-email-input" /></div>
        <div className="space-y-1.5"><Label>{t("f.phone")}</Label><Input value={f.phone} onChange={set("phone")} data-testid="register-phone-input" /></div>
        <div className="space-y-1.5"><Label>{t("f.password")}</Label><Input type="password" required minLength={6} value={f.password} onChange={set("password")} data-testid="register-password-input" /></div>
        <Button type="submit" disabled={busy} className="w-full h-11 rounded-full bg-teal-600 hover:bg-teal-700" data-testid="register-submit-btn">{busy && <Loader2 className="h-4 w-4 me-2 animate-spin" />}{t("register.submit")}</Button>
      </form>
      <Link to="/login" className="block mt-5 text-sm text-teal-700 hover:underline" data-testid="goto-login-link">{t("login.haveacct")}</Link>
    </Shell>
  );
}
